import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Play, TrendingUp, Users, Lightbulb, ArrowRight } from 'lucide-react';
import { CTAButton } from './CTAButton';
const days = [{
  day: 'Día 1',
  icon: <Lightbulb className="w-5 h-5" data-id="element-34" />,
  quote: '"Hoy ayudé a una persona a detectar una oportunidad rentable sin tener miles de seguidores. Te explico cómo pensamos el mercado."',
  tag: 'Oportunidad de mercado',
  color: 'cyan',
  gradient: 'from-cyan-500 to-blue-600',
  bg: 'bg-cyan-500/10',
  border: 'border-cyan-500/20',
  text: 'text-cyan-400'
}, {
  day: 'Día 2',
  icon: <TrendingUp className="w-5 h-5" data-id="element-35" />,
  quote: '"La mayoría no vende porque intenta vender antes de estructurar su oferta. Te muestro el error."',
  tag: 'Estructura de oferta',
  color: 'purple',
  gradient: 'from-purple-500 to-violet-600',
  bg: 'bg-purple-500/10',
  border: 'border-purple-500/20',
  text: 'text-purple-400'
}, {
  day: 'Día 3',
  icon: <Users className="w-5 h-5" data-id="element-36" />,
  quote: '"Si tienes 300 seguidores puedes vender. El problema no es tu audiencia, es tu propuesta."',
  tag: 'Propuesta de valor',
  color: 'emerald',
  gradient: 'from-emerald-500 to-green-600',
  bg: 'bg-emerald-500/10',
  border: 'border-emerald-500/20',
  text: 'text-emerald-400'
}];
function VideoCard({
  item,
  index
}: {
  item: (typeof days)[0];
  index: number;
}) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.15,
    ease: 'easeOut'
  }} onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)} className="group relative cursor-default" data-id="element-37">
      <div className={`relative rounded-2xl bg-[#0c0c20] border ${item.border} overflow-hidden transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,0,0,0.4)]`} data-id="element-38">
        {/* Fake video thumbnail area */}
        <div className="relative aspect-video bg-gradient-to-br from-[#0a0a1a] to-[#12122a] overflow-hidden" data-id="element-39">
          {/* Scanline overlay */}
          <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(0,0,0,0.15)_2px,rgba(0,0,0,0.15)_4px)] pointer-events-none z-10" data-id="element-40" />

          {/* Gradient background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-10`} data-id="element-41" />

          {/* Floating orb */}
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-br ${item.gradient} opacity-20 blur-[40px]`} data-id="element-42" />

          {/* Day label */}
          <div className={`absolute top-4 left-4 z-20 px-3 py-1 rounded-full ${item.bg} border ${item.border} backdrop-blur-sm`} data-id="element-43">
            <span className={`text-xs font-bold uppercase tracking-widest ${item.text}`} data-id="element-44">
              {item.day}
            </span>
          </div>

          {/* Play button */}
          <motion.div animate={hovered ? {
          scale: 1.1
        } : {
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="absolute inset-0 flex items-center justify-center z-20" data-id="element-45">
            <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-[0_0_30px_rgba(0,0,0,0.5)] group-hover:shadow-[0_0_40px_rgba(0,212,255,0.3)] transition-shadow duration-500`} data-id="element-46">
              <Play className="w-6 h-6 text-white fill-white ml-1" data-id="element-47" />
            </div>
          </motion.div>

          {/* Waveform decoration */}
          <div className="absolute bottom-4 left-4 right-4 z-20 flex items-end gap-0.5 h-6 opacity-40" data-id="element-48">
            {Array.from({
            length: 40
          }).map((_, i) => <div key={i} className={`flex-1 rounded-full bg-gradient-to-t ${item.gradient}`} style={{
            height: `${20 + Math.sin(i * 0.8) * 60 + Math.random() * 20}%`
          }} data-id="element-49" />)}
          </div>
        </div>

        {/* Card content */}
        <div className="p-6" data-id="element-50">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${item.bg} border ${item.border} mb-4`} data-id="element-51">
            <span className={item.text} data-id="element-52">{item.icon}</span>
            <span className={`text-xs font-semibold ${item.text}`} data-id="element-53">
              {item.tag}
            </span>
          </div>

          <p className="text-gray-300 text-sm leading-relaxed italic" data-id="element-54">
            {item.quote}
          </p>

          <div className="mt-4 flex items-center gap-2" data-id="element-55">
            <div className={`h-0.5 flex-1 bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity duration-500`} data-id="element-56" />
            <span className="text-xs text-gray-600" data-id="element-57">micro-transformación</span>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function DailyInsights() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-24 px-4 bg-[#050510] relative overflow-hidden" data-id="element-58">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-59">
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-cyan-900/6 rounded-full blur-[150px]" data-id="element-60" />
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-purple-900/6 rounded-full blur-[150px]" data-id="element-61" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10" ref={ref} data-id="element-62">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-63">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-64">
            <Play className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400" data-id="element-65" />
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400" data-id="element-66">
              Contenido que convierte
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4" data-id="element-67">
            Cada video genera una{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400" data-id="element-68">
              micro-transformación
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto" data-id="element-69">
            Así es como enseño. Así es como aprenderás.
          </p>
        </motion.div>

        {/* Video Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-70">
          {days.map((item, index) => <VideoCard key={item.day} item={item} index={index} data-id="element-71" />)}
        </div>

        {/* Closing statement */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.5
      }} className="relative rounded-3xl bg-gradient-to-br from-[#0c0c20] to-[#10102a] border border-white/[0.07] p-8 md:p-12 text-center overflow-hidden" data-id="element-72">
          {/* Glow */}
          <div className="absolute inset-0 pointer-events-none" data-id="element-73">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[200px] bg-gradient-to-r from-cyan-500/5 to-purple-500/5 rounded-full blur-[80px]" data-id="element-74" />
          </div>

          <div className="relative z-10" data-id="element-75">
            <div className="flex justify-center mb-6" data-id="element-76">
              <div className="flex -space-x-1" data-id="element-77">
                {['bg-cyan-500', 'bg-purple-500', 'bg-emerald-500'].map((c, i) => <div key={i} className={`w-3 h-3 rounded-full ${c} ring-2 ring-[#0c0c20]`} data-id="element-78" />)}
              </div>
            </div>

            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-79">
              "Por eso estructuré todo esto en un{' '}
              <span className="text-white font-bold" data-id="element-80">programa de 15 días</span>{' '}
              donde te llevo paso a paso."
            </p>

            <div className="flex justify-center" data-id="element-81">
              <CTAButton text="QUIERO EMPEZAR HOY" size="lg" data-id="element-82" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}